import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GalleryLightbox from '../GalleryLightbox/GalleryLightbox';
import './gallery.scss';

export interface GalleryImage {
    src: string;
    alt?: string;
}

export interface GalleryProps {
    images: Array<string | GalleryImage>; // список изображений
    initialIndex?: number; // стартовый индекс
    onIndexChange?: (index: number) => void; // коллбек изменения индекса
    loop?: boolean; // зацикливание
    autoPlay?: boolean; // автопрокрутка
    autoPlayInterval?: number; // интервал автопрокрутки
    showArrows?: boolean; // показывать стрелки
    showIndicators?: boolean; // показывать индикаторы
    wrapperClassName?: string; // класс контейнера
    imageClassName?: string; // класс тега img
    arrowClassName?: string; // базовый класс кнопок-стрелок
    indicatorClassName?: string; // класс точки-индикатора
    indicatorActiveClassName?: string; // класс активной точки
    ariaLabel?: string; // aria-label региона
}

const toImageObjects = (items: Array<string | GalleryImage>): GalleryImage[] =>
    items.filter(Boolean).map((it) => (typeof it === 'string' ? { src: it } : it)) as GalleryImage[];

const swipeThreshold = 30; // px

const Gallery: React.FC<GalleryProps> = ({
    images,
    initialIndex = 0,
    onIndexChange,
    loop = true,
    autoPlay = false,
    autoPlayInterval = 5000,
    showArrows = true,
    showIndicators = true,
    wrapperClassName = 'u-gallery', // дефолтный класс
    imageClassName = 'u-gallery__image',
    arrowClassName = 'u-gallery__arrow',
    indicatorClassName = 'u-gallery__dot',
    indicatorActiveClassName = 'u-gallery__dot--active',
    ariaLabel = 'image gallery',
}) => {
    const imgs = useMemo(() => toImageObjects(images), [images]);
    const [index, setIndex] = useState(() => Math.min(Math.max(initialIndex, 0), Math.max(imgs.length - 1, 0)));
    const touchStartX = useRef<number | null>(null);
    const autoRef = useRef<number | null>(null);

    // ===== миниатюры / прокрутка =====
    const thumbsScrollRef = useRef<HTMLDivElement | null>(null);
    const [canScrollUp, setCanScrollUp] = useState(false);
    const [canScrollDown, setCanScrollDown] = useState(false);
    const imgsKey = useMemo(() => imgs.map((i) => i.src).join('|'), [imgs]);

    const updateThumbsScrollState = useCallback(() => {
        const el = thumbsScrollRef.current;
        if (!el) {
            setCanScrollUp(false);
            setCanScrollDown(false);
            return;
        }
        const { scrollTop, scrollHeight, clientHeight } = el;
        setCanScrollUp(scrollTop > 0);
        setCanScrollDown(scrollTop + clientHeight < scrollHeight - 1);
    }, []);

    const scrollThumbsBy = useCallback((dir: 1 | -1) => {
        const el = thumbsScrollRef.current;
        if (!el) return;
        const delta = Math.max(120, Math.floor(el.clientHeight * 0.8));
        el.scrollBy({ top: dir * delta, behavior: 'smooth' });
    }, []);

    useEffect(() => {
        const el = thumbsScrollRef.current;
        if (!el) return;
        updateThumbsScrollState();
        const onScroll = () => updateThumbsScrollState();
        const onResize = () => updateThumbsScrollState();
        el.addEventListener('scroll', onScroll);
        window.addEventListener('resize', onResize);
        return () => {
            el.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onResize);
        };
    }, [updateThumbsScrollState]);

    // Автопрокрутка
    useEffect(() => {
        if (!autoPlay || imgs.length <= 1) return;
        autoRef.current = window.setInterval(goNext, autoPlayInterval);
        return () => {
            if (autoRef.current) window.clearInterval(autoRef.current);
            autoRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoPlay, autoPlayInterval, imgs.length]);

    const emitChange = useCallback(
        (next: number) => {
            setIndex(next);
            onIndexChange?.(next);
        },
        [onIndexChange],
    );

    const trackRef = useRef<HTMLDivElement | null>(null);
    const [isAnimating, setIsAnimating] = useState(true);

    const goNext = useCallback(() => {
        if (imgs.length <= 1) return;
        if (index + 1 < imgs.length) {
            setIsAnimating(true);
            emitChange(index + 1);
        } else if (loop) {
            setIsAnimating(false);
            emitChange(0);
        }
    }, [emitChange, imgs.length, index, loop]);

    const goPrev = useCallback(() => {
        if (imgs.length <= 1) return;
        if (index - 1 >= 0) {
            setIsAnimating(true);
            emitChange(index - 1);
        } else if (loop) {
            setIsAnimating(false);
            emitChange(imgs.length - 1);
        }
    }, [emitChange, imgs.length, index, loop]);

    // Управление клавиатурой (влево/вправо)
    const onKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                goPrev();
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                goNext();
            }
        },
        [goNext, goPrev],
    );

    // Свайпы
    const onTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.touches[0]?.clientX ?? null;
    };
    const onTouchEnd = (e: React.TouchEvent) => {
        if (touchStartX.current == null) return;
        const dx = (e.changedTouches[0]?.clientX ?? touchStartX.current) - touchStartX.current;
        if (dx > swipeThreshold) goPrev();
        else if (dx < -swipeThreshold) goNext();
        touchStartX.current = null;
    };

    // Автоподскролл активной миниатюры в область видимости
    useEffect(() => {
        const scroller = thumbsScrollRef.current;
        if (!scroller) return;
        const thumb = scroller.querySelector<HTMLElement>(`[data-thumb-index="${index}"]`);
        if (thumb) {
            thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        }
        updateThumbsScrollState();
    }, [index, updateThumbsScrollState]);

    useEffect(() => {
        setIsAnimating(false);
        setIndex(Math.min(Math.max(initialIndex, 0), Math.max(imgs.length - 1, 0)));
    }, [imgsKey, initialIndex, imgs.length]);

    // ZOOM
    const stageRef = useRef<HTMLDivElement | null>(null);
    const imgElRef = useRef<HTMLImageElement | null>(null);
    const [isZoomed, setIsZoomed] = useState(false);
    const [zoomEnabled, setZoomEnabled] = useState(false);
    const rafRef = useRef<number | null>(null);
    const pendingPosRef = useRef<{ x: number; y: number } | null>(null);

    // включаем zoom только для устройств с "тонким" указателем и шириной >= 810px
    useEffect(() => {
        const update = () => {
            const mq = typeof window !== 'undefined' ? window.matchMedia('(hover: hover) and (pointer: fine)') : null;
            setZoomEnabled(!!mq?.matches && window.innerWidth >= 810);
        };
        update();
        const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
        const onChange = () => update();
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else mq.addListener(onChange);
        window.addEventListener('resize', onChange);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', onChange);
            else mq.removeListener(onChange);
            window.removeEventListener('resize', onChange);
        };
    }, []);

    const pauseAutoPlay = useCallback(() => {
        if (autoRef.current) {
            window.clearInterval(autoRef.current);
            autoRef.current = null;
        }
    }, []);

    const resumeAutoPlay = useCallback(() => {
        if (autoPlay && imgs.length > 1 && autoRef.current == null) {
            autoRef.current = window.setInterval(goNext, autoPlayInterval);
        }
    }, [autoPlay, autoPlayInterval, goNext, imgs.length]);

    const handleMouseEnter = useCallback(() => {
        if (!zoomEnabled) return;
        setIsZoomed(true);
        pauseAutoPlay();
    }, [zoomEnabled, pauseAutoPlay]);

    const handleMouseLeave = useCallback(() => {
        setIsZoomed(false);
        if (imgElRef.current) imgElRef.current.style.transformOrigin = '';
        resumeAutoPlay();
    }, [resumeAutoPlay]);

    const handleMouseMove = useCallback(
        (e: React.MouseEvent) => {
            if (!zoomEnabled || !isZoomed || !stageRef.current || !imgElRef.current) return;
            const rect = stageRef.current.getBoundingClientRect();
            const x = Math.min(Math.max(e.clientX - rect.left, 0), rect.width);
            const y = Math.min(Math.max(e.clientY - rect.top, 0), rect.height);
            const percentX = (x / rect.width) * 100;
            const percentY = (y / rect.height) * 100;
            pendingPosRef.current = { x: percentX, y: percentY };
            if (rafRef.current == null) {
                rafRef.current = requestAnimationFrame(() => {
                    if (imgElRef.current && pendingPosRef.current) {
                        imgElRef.current.style.transformOrigin = `${pendingPosRef.current.x}% ${pendingPosRef.current.y}%`;
                    }
                    rafRef.current = null;
                });
            }
        },
        [zoomEnabled, isZoomed],
    );

    useEffect(() => {
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, []);

    // при смене изображения сбрасываем zoom
    useEffect(() => {
        setIsZoomed(false);
        if (imgElRef.current) imgElRef.current.style.transformOrigin = '';
    }, [index]);

    // POPUP
    const [isLightboxOpen, setLightboxOpen] = useState(false);
    const openLightbox = useCallback(() => setLightboxOpen(true), []);
    const closeLightbox = useCallback(() => setLightboxOpen(false), []);

    useEffect(() => {
        if (isLightboxOpen) pauseAutoPlay();
        else resumeAutoPlay();
    }, [isLightboxOpen, pauseAutoPlay, resumeAutoPlay]);

    if (!imgs.length) {
        // если изображений нет - показываем логотип
        const logoSrc = `${process.env.PUBLIC_URL || ''}/logo.png`;
        return (
            <div className={`${wrapperClassName} u-gallery`} role="region" aria-label={ariaLabel} tabIndex={0} onKeyDown={onKeyDown}>
                <div className="u-gallery__stage">
                    <div style={{ width: '100%', aspectRatio: '1/1', display: 'grid', placeItems: 'center' }}>
                        <img src={logoSrc} alt="Логотип" style={{ maxWidth: '60%', maxHeight: '60%', opacity: 0.6 }} draggable={false} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`${wrapperClassName} u-gallery`}
            role="region"
            aria-label={ariaLabel}
            tabIndex={0}
            onKeyDown={onKeyDown}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
        >
            {/* Лента миниатюр слева (видна на > 810px) */}
            <aside className="u-gallery__thumbs">
                <button
                    type="button"
                    className={`u-gallery__thumbs-btn u-gallery__thumbs-btn--up${canScrollUp ? '' : ' is-hidden'}`}
                    onClick={() => scrollThumbsBy(-1)}
                    aria-label="Прокрутить миниатюры вверх"
                    // title="Вверх"
                >
                    &#9650;
                </button>
                <div className="u-gallery__thumbs-scroll" ref={thumbsScrollRef}>
                    {imgs.map((img, i) => {
                        const active = i === index;
                        return (
                            <button
                                key={i}
                                type="button"
                                className={`u-gallery__thumb${active ? ' u-gallery__thumb--active' : ''}`}
                                onClick={() => emitChange(i)}
                                aria-label={`Показать изображение ${i + 1}`}
                                aria-current={active ? 'true' : undefined}
                                data-thumb-index={i}
                            >
                                <img src={img.src} alt={img.alt ?? `Миниатюра ${i + 1}`} draggable={false} />
                            </button>
                        );
                    })}
                </div>
                <button
                    type="button"
                    className={`u-gallery__thumbs-btn u-gallery__thumbs-btn--down${canScrollDown ? '' : ' is-hidden'}`}
                    onClick={() => scrollThumbsBy(1)}
                    aria-label="Прокрутить миниатюры вниз"
                    // title="Вниз"
                >
                    &#9660;
                </button>
            </aside>

            <div className="u-gallery__stage" ref={stageRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onMouseMove={handleMouseMove}>
                {showArrows && imgs.length > 1 && (
                    <button
                        type="button"
                        className={`${arrowClassName} ${arrowClassName}--prev`}
                        onClick={goPrev}
                        title="Предыдущее фото"
                        aria-label="Предыдущее фото"
                    >
                        &#8249;
                    </button>
                )}
                <div className="u-gallery__track" ref={trackRef} style={{ transform: `translateX(-${index * 100}%)`, transition: isAnimating ? undefined : 'none' }}>
                    {imgs.map((img, i) => {
                        const isActive = i === index;
                        return (
                            <img
                                key={i}
                                ref={isActive ? imgElRef : null}
                                src={img.src}
                                alt={img.alt ?? `Изображение ${i + 1} из ${imgs.length}`}
                                className={`${imageClassName}${isActive && isZoomed ? ' is-zoomed' : ''}`}
                                style={isActive && isZoomed ? { transform: 'scale(2)' } : undefined}
                                draggable={false}
                                onClick={isActive ? openLightbox : undefined}
                            />
                        );
                    })}
                </div>
                {showArrows && imgs.length > 1 && (
                    <button
                        type="button"
                        className={`${arrowClassName} ${arrowClassName}--next`}
                        onClick={goNext}
                        title="Следующее фото"
                        aria-label="Следующее фото"
                    >
                        &#8250;
                    </button>
                )}
                {showIndicators && imgs.length > 1 && (
                    <div className={`${wrapperClassName.replace(/__gallery$/, '__indicators')} u-gallery__indicators`}>
                        {imgs.map((_, i) => {
                            const isActive = i === index;
                            const cls = `${indicatorClassName}${isActive ? ` ${indicatorActiveClassName}` : ''}`;
                            return (
                                <span
                                    key={i}
                                    className={cls}
                                    aria-label={`Перейти к изображению ${i + 1}`}
                                    aria-current={isActive ? 'true' : undefined}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => {
                                        setIsAnimating(true);
                                        emitChange(i);
                                    }}
                                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (setIsAnimating(true), emitChange(i))}
                                />
                            );
                        })}
                    </div>
                )}
            </div>
            {isLightboxOpen && <GalleryLightbox images={imgs} initialIndex={index} onClose={closeLightbox} onIndexChange={(i) => emitChange(i)} />}
        </div>
    );
};

export default memo(Gallery);
